import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Ban, Plus, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function StrikeListPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Strike List</h1>
        <p className="text-lg text-gray-700 mb-6">
          The Strike List feature allows you to customize your content by defining words, phrases, and writing patterns you want the AI to avoid. This helps ensure your content matches your brand voice and preferences.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>What is a Strike List?</h2>
        <p>
          A Strike List is a collection of words, phrases, literary devices, and language patterns that you want the AI to avoid when generating your content. 
          Think of it as a personalized "do not use" list that helps shape your content to match your preferences.
        </p>
        
        <h3>Why Use a Strike List?</h3>
        <p>
          Many AI-generated texts contain overused phrases and clichés that can make content feel generic or artificial. 
          The Strike List helps you eliminate these issues by instructing the AI to avoid specific language patterns you dislike.
        </p>
        
        <div className="not-prose bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 mb-1">Common AI Writing Problems</h4>
              <p className="text-sm text-yellow-700">
                Many users find that AI writing tools overuse certain phrases like "in today's fast-paced world," "unlock the potential," "game-changer," and "it's worth noting." The Strike List lets you eliminate these clichés from your content.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-100 p-2 rounded-lg">
              <Ban className="w-5 h-5 text-red-700" />
            </div>
            <h3 className="text-lg font-semibold">Strike List Categories</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            The Strike List feature is organized into four distinct categories, each addressing a different aspect of language:
          </p>
          
          <div className="space-y-3">
            <div className="p-3 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-1">Words</h4>
              <p className="text-sm text-gray-600">Individual words you want to avoid completely.</p>
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">actually</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">leverage</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">utilize</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">simply</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">just</span>
              </div>
            </div>
            
            <div className="p-3 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-1">Phrases</h4>
              <p className="text-sm text-gray-600">Multi-word expressions and idioms to avoid.</p>
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">in today's world</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">game changer</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">at the end of the day</span>
              </div>
            </div>
            
            <div className="p-3 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-1">Similarities</h4>
              <p className="text-sm text-gray-600">Comparisons or metaphors to avoid.</p>
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">tip of the iceberg</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">like a needle in a haystack</span>
              </div>
            </div>
            
            <div className="p-3 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-1">Literary Devices</h4>
              <p className="text-sm text-gray-600">Writing techniques or patterns to avoid.</p>
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">rhetorical questions</span>
                <span className="bg-gray-200 text-gray-800 px-2 py-0.5 text-xs rounded-full">excessive alliteration</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Plus className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Managing Your Strike List</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            You can easily add, remove, and manage items in your Strike List through the settings menu.
          </p>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-base mb-2">How to Access the Strike List</h4>
              <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-600">
                <li>Click on the <strong>Settings</strong> icon in the top navigation</li>
                <li>Select <strong>Manage Strike List</strong> from the dropdown menu</li>
                <li>The Strike List management dialog will appear</li>
              </ol>
            </div>
            
            <div>
              <h4 className="font-medium text-base mb-2">Adding Items to Your Strike List</h4>
              <div className="border rounded-md p-3 bg-gray-50">
                <div className="flex gap-2 mb-3">
                  <input 
                    type="text"
                    placeholder="Enter words, separated by commas"
                    className="flex-1 px-3 py-1.5 border rounded-md bg-white"
                    disabled
                  />
                  <Button disabled className="flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  You can add multiple items at once by separating them with commas.
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-base mb-2">Removing Items from Your Strike List</h4>
              <div className="border rounded-md p-3 bg-gray-50">
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 border">
                  <span className="text-sm">leverage</span>
                  <button className="opacity-50 group-hover:opacity-100 transition-opacity" disabled>
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 border">
                  <span className="text-sm">in today's fast-paced world</span>
                  <button className="opacity-50 group-hover:opacity-100 transition-opacity" disabled>
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Click the X icon to remove an item from your Strike List.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <h2 className="text-xl font-bold mb-4">Strike List Examples</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-3">Common Business Clichés to Avoid</h3>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              <div className="bg-gray-50 p-2 text-sm rounded border">leverage</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">synergy</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">robust</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">cutting-edge</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">game-changer</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">revolutionary</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">disruptive</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">ecosystem</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">moving forward</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">think outside the box</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">at the end of the day</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">touch base</div>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-lg mb-3">Weak Modifiers</h3>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              <div className="bg-gray-50 p-2 text-sm rounded border">very</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">really</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">quite</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">just</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">simply</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">actually</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">basically</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">literally</div>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-lg mb-3">AI Writing Red Flags</h3>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div className="bg-gray-50 p-2 text-sm rounded border">in today's fast-paced world</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">it's worth noting that</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">it goes without saying</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">in this comprehensive guide</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">unlock the potential</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">delve into</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">navigate the landscape</div>
              <div className="bg-gray-50 p-2 text-sm rounded border">harness the power</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border rounded-lg p-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Strike List Best Practices</h3>
        
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Start with Common Offenders</h4>
            <p className="text-gray-600">
              Begin by adding the most common AI writing clichés and business jargon to your Strike List. This creates an immediate improvement in content quality.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Review and Refine</h4>
            <p className="text-gray-600">
              After generating several posts, review them for patterns or phrases you dislike, then add those to your Strike List for future content.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Be Specific</h4>
            <p className="text-gray-600">
              Instead of broad terms that might limit useful vocabulary, focus on specific phrases and patterns you want to avoid.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Consider Industry Context</h4>
            <p className="text-gray-600">
              Add industry-specific jargon or overused terms that are particular to your field but that you want to avoid.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/posts/export">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Export Options
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/settings">
            Next: Account Settings
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}